<template>
  <b-modal
    :id="modalId"
    v-model="show"
    :title="modalTitle"
    centered
    modal-class="bm-modal"
    dialog-class="bm-modal__dialog"
    content-class="bm-modal__content"
    header-class="bm-modal__header"
    body-class="bm-modal__body"
    footer-class="bm-modal__footer"
    hide-footer
    @hidden="onHidden"
  >
    <!-- Summary banner -->
    <div class="bm-modal__topbar d-flex align-items-center w-100">
      <div class="bm-modal__badges d-flex align-items-center">
        <b-badge variant="warning" class="mr-2"
          v-b-tooltip.hover.top="msg.skippedBadgeTooltip">
          {{ msg.skippedBadgeLabel }}: {{ skippedList.length }}
        </b-badge>
        <b-badge :variant="remainingList.length ? 'success' : 'danger'"
          v-b-tooltip.hover.top="remainingList.length ? msg.remainingBadgeTooltipHas : msg.remainingBadgeTooltipNone">
          {{ msg.remainingBadgeLabel }}: {{ remainingList.length }}
        </b-badge>
      </div>

      <div class="ml-auto d-flex align-items-center" style="gap:8px;">
        <b-button size="sm" variant="outline-secondary" @click="copyToClipboard"
          v-b-tooltip.hover.top="'Copy skipped device IDs to clipboard'">
          Copy Skipped
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="exportCSV"
          v-b-tooltip.hover.top="'Download skipped device IDs as CSV'">
          Export CSV
        </b-button>
      </div>
    </div>

    <!-- Contextual info message -->
    <p class="bm-modal__info-text" v-if="remainingList.length">
      <strong>{{ remainingList.length }}</strong> device{{ remainingList.length !== 1 ? 's' : '' }} {{ msg.infoRemainingVerb }}.
      <strong>{{ skippedList.length }}</strong> device{{ skippedList.length !== 1 ? 's' : '' }} {{ skippedList.length !== 1 ? 'are' : 'is' }} {{ msg.infoSkippedState }} and {{ skippedList.length !== 1 ? 'have' : 'has' }} been removed from the selection.
    </p>
    <p class="bm-modal__info-text bm-modal__info-text--warn" v-else>
      {{ msg.infoTextWarnNone }}
    </p>

    <div class="bm-modal__search" style="margin-top:10px;">
      <b-input v-model="search" placeholder="Search by Device ID..." />
      <b-button size="sm" class="bm-modal__clear" :disabled="!search" @click="search = ''">Clear</b-button>
    </div>

    <!-- Manual tab nav — avoids b-tabs display:none toggle which causes height flicker -->
    <div style="margin-top:10px;">
      <ul class="sdm-tab-nav">
        <li class="sdm-tab-nav__item" :class="{ 'sdm-tab-nav__item--active': activeTab === 'skipped' }" @click="activeTab = 'skipped'">
          {{ msg.skippedTabLabel }} <b-badge variant="warning" class="ml-1">{{ filteredSkipped.length }}</b-badge>
        </li>
        <li class="sdm-tab-nav__item" :class="{ 'sdm-tab-nav__item--active': activeTab === 'remaining' }" @click="activeTab = 'remaining'">
          {{ msg.remainingTabLabel }} <b-badge :variant="remainingList.length ? 'success' : 'secondary'" class="ml-1">{{ filteredRemaining.length }}</b-badge>
        </li>
      </ul>
      <div class="sdm-tab-panels">
        <div v-show="activeTab === 'skipped'" class="sdm-tab-panel">
          <p class="bm-modal__tab-hint">{{ msg.skippedTabHint }}</p>
          <b-table
            small striped hover
            :items="filteredSkipped.map(id => ({ deviceId: id }))"
            :fields="fields"
            show-empty
            empty-text="No devices to show"
            class="bm-modal__table"
          >
            <template #cell(deviceId)="row">{{ row.item.deviceId }}</template>
          </b-table>
        </div>

        <div v-show="activeTab === 'remaining'" class="sdm-tab-panel">
          <p class="bm-modal__tab-hint">
            <span v-if="remainingList.length">{{ msg.remainingTabHintHas }}</span>
            <span v-else>{{ msg.remainingTabHintNone }}</span>
          </p>
          <b-table
            small striped hover
            :items="filteredRemaining.map(id => ({ deviceId: id }))"
            :fields="fields"
            show-empty
            empty-text="No devices to show"
            class="bm-modal__table"
          >
            <template #cell(deviceId)="row">{{ row.item.deviceId }}</template>
          </b-table>
        </div>
      </div>
    </div>

    <div class="bm-modal__footer-actions" style="margin-top:12px; text-align:right;">
      <b-button size="sm" variant="primary" @click="close">Close</b-button>
    </div>
  </b-modal>
</template>

<script>
import BedManagerService from "../BedManager.service";
import { SKIPPED_DEVICES_MESSAGES } from "../bed-manager-constants";
export default {
  name: 'SkippedDevicesModal',
  props: {
    value: { type: Boolean, default: false },
    skipped: { type: Array, default: () => [] },
    remaining: { type: Array, default: () => [] },
    mode: { type: String, default: 'preview' }, // 'preview' | 'post-submit'
    modalId: { type: String, default: 'skipped-devices-modal' },
    messages: { type: Object, default: () => SKIPPED_DEVICES_MESSAGES.OTA }
  },
  data() {
    return {
      search: '',
      activeTab: 'skipped',
      fields: [ { key: 'deviceId', label: 'Device ID', sortable: true } ],
    };
  },
  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); }
    },
    msg() {
      return { ...SKIPPED_DEVICES_MESSAGES.OTA, ...(this.messages || {}) };
    },
    modalTitle() {
      return this.mode === 'preview' ? this.msg.titlePreview : this.msg.titlePostSubmit;
    },
    skippedList() {
      return Array.isArray(this.skipped) ? this.skipped : [];
    },
    remainingList() {
      return Array.isArray(this.remaining) ? this.remaining : [];
    },
    normalizedSearch() {
      return String(this.search || '').trim().toLowerCase();
    },
    filteredSkipped() {
      const q = this.normalizedSearch;
      if (!q) return this.skippedList;
      return this.skippedList.filter(id => String(id).toLowerCase().includes(q));
    },
    filteredRemaining() {
      const q = this.normalizedSearch;
      if (!q) return this.remainingList;
      return this.remainingList.filter(id => String(id).toLowerCase().includes(q));
    },
    // legacy alias used by copyToClipboard / exportCSV (copies skipped list)
    filteredList() {
      return this.filteredSkipped;
    }
  },
  methods: {
    close() {
      this.show = false;
    },
    onHidden() {
      this.search = '';
      this.$emit('closed');
    },
    async copyToClipboard() {
      const text = (this.filteredList || []).join('\n');
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.$root.$emit('bv::show::toast', { message: 'Copied skipped device IDs to clipboard', variant: 'success' });
      } catch (e) {
        console.error('Copy failed', e);
        this.$root.$emit('bv::show::toast', { message: 'Copy failed', variant: 'danger' });
      }
    },
    exportCSV() {
      const headers = ['Device ID'];
      const rows = (this.filteredList || []).map(id => [id]);
      const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
      if (BedManagerService && BedManagerService.exportToCSV) {
        BedManagerService.exportToCSV(csvContent, 'Skipped_Devices');
      } else {
        // fallback to in-component download if service unavailable
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'skipped_devices.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }
  }
}
</script>

<style scoped>
.bm-modal__search { display:flex; gap:8px; align-items:center }
.bm-modal__topbar { display:flex; align-items:center }
.bm-modal__badges { display:flex; gap:8px; }
.bm-modal__footer-actions { display:flex; justify-content:flex-end }
.bm-modal__info-text {
  min-height: 42px;
  margin: 10px 0 0;
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}
.bm-modal__info-text--warn {
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 12px;
}
.bm-modal__tab-hint {
  font-size: 12px;
  color: #666;
  margin: 4px 0 8px;
  font-style: italic;
}
.sdm-tab-nav {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  border-bottom: 2px solid #dee2e6;
}
.sdm-tab-nav__item {
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #6c757d;
  border: 1px solid transparent;
  border-bottom: none;
  margin-bottom: -2px;
  border-radius: 4px 4px 0 0;
  user-select: none;
}
.sdm-tab-nav__item:hover {
  color: #495057;
  background: #f8f9fa;
}
.sdm-tab-nav__item--active {
  color: #495057;
  background: #fff;
  border-color: #dee2e6;
  border-bottom-color: #fff;
  font-weight: 600;
}
.sdm-tab-panels {
  height: 310px;
  margin-top: 8px;
}
.sdm-tab-panel {
  height: 100%;
  overflow-y: auto;
}

/* Match Close button styling for the topbar action buttons */
.bm-modal__topbar .btn {
  color: #ffffff !important;
  background-color: #1dd8f6 !important;
  border: none !important;
  border-radius: 5px !important;
}
.bm-modal__topbar .btn:hover,
.bm-modal__topbar .btn:focus,
.bm-modal__topbar .btn:active,
.bm-modal__topbar .btn:focus:active {
  /* keep identical appearance on hover/focus/active */
  color: #ffffff !important;
  background-color: #1dd8f6 !important;
  border-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}
</style>
